import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle2, MapPinned, ShieldCheck, Users } from 'lucide-react';
import { scrollToTop } from '../utils/scrollUtils';

const audienceGroups = [
  {
    title: 'Wheelchair users',
    description:
      'Book vehicles with ramps or lifts, secure wheelchair restraints, and space designed for safe seated travel.',
  },
  {
    title: 'Older adults',
    description:
      'Arrange steady, assisted journeys for shopping, social visits, and appointments without relying on last-minute options.',
  },
  {
    title: 'People with medical needs',
    description:
      'Pre-book reliable transport for clinics, dialysis, therapy, and repeat hospital journeys with door-to-door support.',
  },
];

const benefits = [
  'Wheelchair-accessible vehicles matched to your mobility needs',
  'Trained drivers and companions who understand assisted travel',
  'Door-to-door help from pickup through drop-off',
  'Pre-booked rides with clear pickup times and live updates',
];

const steps = [
  {
    title: 'Share journey details',
    description: 'Enter pickup, drop-off, timing, and accessibility requirements in one booking flow.',
  },
  {
    title: 'Get matched',
    description: 'AbleGo matches your trip with the right vehicle and trained support for a safer journey.',
  },
  {
    title: 'Travel with confidence',
    description: 'Your driver arrives on time, assists door-to-door, and completes your planned journey.',
  },
];

const faqs = [
  {
    question: 'How does AbleGo provide accessible transport UK-wide?',
    answer:
      'AbleGo operates across major towns and cities in the UK through a network of vetted drivers and accessible vehicles. You can pre-book journeys in advance for local or longer-distance travel.',
  },
  {
    question: 'Can I book accessible transport UK journeys for medical appointments?',
    answer:
      'Yes. Many passengers use AbleGo for hospital, GP, therapy, and clinic appointments. You can include mobility requirements and timing needs during booking.',
  },
  {
    question: 'Do your vehicles support wheelchair users safely?',
    answer:
      'Yes. Wheelchair-accessible vehicles include secure restraint systems and safe loading options such as ramps or lifts, depending on the vehicle matched to your booking.',
  },
  {
    question: 'Which cities do you cover?',
    answer:
      'AbleGo supports UK-wide coverage, including London, Birmingham, Manchester, Leeds, Glasgow, Liverpool, Bristol, and surrounding areas.',
  },
  {
    question: 'Can I schedule regular transport with AbleGo?',
    answer:
      'Yes. You can arrange recurring journeys for weekly appointments, regular social visits, or ongoing care routines using advance bookings.',
  },
  {
    question: 'Is support available for passengers who need extra assistance?',
    answer:
      'Yes. AbleGo offers trained drivers and companion support for passengers who need practical help before, during, or after travel.',
  },
];

const AccessibleTransportUkPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Accessible Transport UK | Wheelchair & Assisted Rides';
    scrollToTop('instant');
  }, []);

  return (
    <>
      <Helmet>
        <title>Accessible Transport UK | Wheelchair & Assisted Rides</title>
        <meta
          name="description"
          content="Book accessible transport UK-wide with wheelchair vehicles, trained drivers and door-to-door help. Pre-book reliable journeys with AbleGo."
        />
        <link rel="canonical" href="https://ablego.co.uk/accessible-transport-uk" />
      </Helmet>

      <div className="pt-20">
        <section className="bg-gradient-to-br from-blue-700 via-blue-600 to-teal-600 text-white py-16 lg:py-24">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl">
              <p className="text-blue-100 font-semibold tracking-wide uppercase mb-4">Nationwide assisted mobility</p>
              <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
                Accessible Transport UK You Can Trust
              </h1>
              <p className="text-lg lg:text-xl text-blue-50 max-w-3xl mb-8">
                Book reliable journeys with wheelchair-accessible vehicles, trained drivers, and door-to-door help. AbleGo helps you travel safely across the UK.
              </p>
              <Link
                to="/booking"
                className="inline-flex items-center px-7 py-4 rounded-xl bg-white text-blue-700 font-semibold hover:bg-blue-50 transition-colors"
              >
                Book a Ride
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white dark:bg-dark-900">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-8">Who this service is for</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {audienceGroups.map((group) => (
                <article key={group.title} className="p-6 rounded-2xl bg-gray-50 dark:bg-dark-800 border border-gray-100 dark:border-dark-700">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-3">{group.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{group.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50 dark:bg-dark-800">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-8">What you get with AbleGo</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {benefits.map((benefit) => (
                <div key={benefit} className="flex items-start gap-3 p-5 rounded-xl bg-white dark:bg-dark-900 border border-gray-100 dark:border-dark-700">
                  <CheckCircle2 className="w-6 h-6 text-teal-600 mt-0.5" />
                  <p className="text-gray-700 dark:text-gray-200">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-white dark:bg-dark-900">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-8">How it works</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {steps.map((step, index) => (
                <article key={step.title} className="rounded-2xl border border-gray-200 dark:border-dark-700 p-6">
                  <p className="text-sm font-semibold text-blue-600 mb-2">Step {index + 1}</p>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-3">{step.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300">{step.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-blue-600 text-white">
          <div className="container mx-auto px-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <h2 className="text-3xl font-bold mb-3">Need dependable accessible transport UK coverage?</h2>
                <p className="text-blue-100">
                  Plan journeys in London, Birmingham, Manchester, Leeds, Glasgow, Liverpool, Bristol, and many other UK locations.
                </p>
              </div>
              <Link
                to="/booking"
                className="inline-flex justify-center items-center px-7 py-4 rounded-xl bg-white text-blue-700 font-semibold hover:bg-blue-50 transition-colors"
              >
                Arrange Transport
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50 dark:bg-dark-800">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-8">Safety and trust, built into every trip</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <article className="p-6 rounded-2xl bg-white dark:bg-dark-900 border border-gray-100 dark:border-dark-700">
                <ShieldCheck className="w-7 h-7 text-blue-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-2">Vetted professionals</h3>
                <p className="text-gray-600 dark:text-gray-300">Drivers and companions are checked and trained for assisted passenger travel.</p>
              </article>
              <article className="p-6 rounded-2xl bg-white dark:bg-dark-900 border border-gray-100 dark:border-dark-700">
                <Users className="w-7 h-7 text-blue-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-2">Human support</h3>
                <p className="text-gray-600 dark:text-gray-300">Get practical help from doorstep to destination, tailored to your booking needs.</p>
              </article>
              <article className="p-6 rounded-2xl bg-white dark:bg-dark-900 border border-gray-100 dark:border-dark-700">
                <MapPinned className="w-7 h-7 text-blue-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-2">Reliable journey planning</h3>
                <p className="text-gray-600 dark:text-gray-300">Pre-booking and intelligent matching reduce disruption and improve on-time performance.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white dark:bg-dark-900">
          <div className="container mx-auto px-6 max-w-4xl">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-8">Frequently asked questions</h2>
            <div className="space-y-4">
              {faqs.map((faq) => (
                <details key={faq.question} className="group rounded-xl border border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-800 p-5">
                  <summary className="cursor-pointer list-none font-semibold text-gray-900 dark:text-gray-50 flex justify-between items-center">
                    {faq.question}
                    <span className="ml-4 text-blue-600 group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-gray-600 dark:text-gray-300 leading-relaxed">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-gray-900 text-white">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to arrange your next journey?</h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Get the right support, vehicle, and journey timing in one booking flow with AbleGo.
            </p>
            <Link
              to="/booking"
              className="inline-flex items-center px-8 py-4 rounded-xl bg-teal-500 text-white font-semibold hover:bg-teal-400 transition-colors"
            >
              Get Started
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default AccessibleTransportUkPage;
